import type { ViewController } from '@presentation/shell/NexxusShell';
import type { Salarie } from '@shared/types/Salarie';
import type { Immobilisation, CategorieImmo, ModeAmortissement } from '@shared/types/Immobilisation';
import { fmtFCFA, esc, fmtDate } from '@presentation/shell/format';
import { ToastService } from '@presentation/components/Toast';
import { canMutateComptaRecords } from '@application/access/ViewAccessPolicy';
import { uuid } from '@shared/utils/uuid';
import { AmortissementsService } from '@domain/comptabilite/AmortissementsService';
import {
  comptesImmobilisation,
  dureeAmortissement,
  genererEcrituresAcquisition,
  genererEcrituresCession,
  genererEcrituresDotation,
} from '@domain/comptabilite/ImmobilisationRegistre';
import { AuthError } from '@infra/auth/AuthService';
import { expiredLicenceCopy } from '@application/licence/LicenceExpiryCopy';
import {
  bindCrudTable,
  crudActions,
  emptyRow,
  loadRegister,
  openRegisterForm,
  removeById,
  saveRegister,
  tenantIdOf,
  upsertById,
  type FieldDef,
} from '@presentation/shell/localCrud';

function kpi(label: string, value: string): string {
  return `<div class="kpi"><div class="label">${label}</div><div class="value">${value}</div></div>`;
}

function guard(can: boolean): boolean {
  if (can) return true;
  ToastService.warning('Action réservée aux profils autorisés');
  return false;
}

function salarieLabel(salaries: Salarie[], id: string): string {
  const s = salaries.find((x) => x.id === id);
  return s ? `${s.prenom} ${s.nom}` : id || '—';
}

export interface ExerciceLocal {
  id: string;
  annee: string;
  code?: string;
  commentaire?: string;
  date_debut: string;
  date_fin: string;
  statut: 'ouvert' | 'cloture';
}

export function mergeExercices(
  declared: ExerciceLocal[],
  yearsFromBulletins: string[],
  courant: string,
): ExerciceLocal[] {
  const byYear = new Map<string, ExerciceLocal>();
  for (const an of yearsFromBulletins) {
    byYear.set(an, {
      id: `derived-${an}`,
      annee: an,
      date_debut: `${an}-01-01`,
      date_fin: `${an}-12-31`,
      statut: an === courant ? 'ouvert' : 'cloture',
    });
  }
  for (const exo of declared) {
    byYear.set(exo.annee, exo);
  }
  if (!byYear.has(courant)) {
    byYear.set(courant, {
      id: `derived-${courant}`,
      annee: courant,
      date_debut: `${courant}-01-01`,
      date_fin: `${courant}-12-31`,
      statut: 'ouvert',
    });
  }
  return [...byYear.values()].sort((a, b) => b.annee.localeCompare(a.annee));
}

export const exercicesController: ViewController = async (section, ctx, shell) => {
  const tid = tenantIdOf(ctx);
  const canMutate = canMutateComptaRecords(shell.permission);
  const bulletins = await ctx.bulletins.list();
  const tenant = ctx.tenantContext.tryGetTenant();
  const currentYear = tenant?.exercice_courant ?? String(new Date().getFullYear());

  const years = new Set<string>();
  const masse: Record<string, { count: number; masse: number }> = {};
  for (const b of bulletins) {
    const an = (b.periode || '').slice(0, 4);
    if (!an) continue;
    years.add(an);
    const cur = masse[an] ?? { count: 0, masse: 0 };
    cur.count += 1;
    cur.masse += b.brut_imposable || 0;
    masse[an] = cur;
  }

  let declared = await loadRegister<ExerciceLocal>(tid, 'exercices');
  const render = (): void => {
    const annees = mergeExercices(declared, [...years], currentYear);
    const grid = section.querySelector<HTMLElement>('#exo-kpi-grid');
    if (grid)
      grid.innerHTML =
        kpi('Exercices', String(annees.length)) + kpi('Exercice courant', esc(currentYear));
    const tbody = section.querySelector('#exo-table tbody');
    const empty = section.querySelector<HTMLElement>('#exo-empty');
    if (empty) empty.style.display = annees.length === 0 ? '' : 'none';
    if (tbody) {
      tbody.innerHTML = annees
        .map((exo) => {
          const d = masse[exo.annee] ?? { count: 0, masse: 0 };
          const persisted = !exo.id.startsWith('derived-');
          const actions = persisted ? crudActions(exo.id) : `<span class="pill">Observé</span>`;
          return `<tr>
            <td class="mono">${esc(exo.code || `EX-${exo.annee}`)}</td>
            <td>${esc(fmtDate(exo.date_debut))} → ${esc(fmtDate(exo.date_fin))}${exo.commentaire ? `<br><span style="font-size:11px;color:var(--gris);">${esc(exo.commentaire)}</span>` : ''}</td>
            <td class="num">${d.count}</td>
            <td class="num">${esc(fmtFCFA(d.masse))}</td>
            <td class="num">—</td>
            <td>${exo.statut === 'ouvert' ? '<span class="pill success">Ouvert</span>' : '<span class="pill">Clôturé</span>'}</td>
            <td class="num">${actions}</td>
          </tr>`;
        })
        .join('');
    }
  };
  render();

  type ModalEl = HTMLElement & { open: () => void; close: () => void };
  const modal = section.querySelector<ModalEl>('#exo-modal');
  const inputCode = section.querySelector<HTMLInputElement>('#exo-code');
  const inputAnnee = section.querySelector<HTMLInputElement>('#exo-annee');
  const inputDebut = section.querySelector<HTMLInputElement>('#exo-debut');
  const inputFin = section.querySelector<HTMLInputElement>('#exo-fin');
  const inputStatut = section.querySelector<HTMLSelectElement>('#exo-statut');
  const inputComm = section.querySelector<HTMLTextAreaElement>('#exo-comm');
  let editingId: string | null = null;

  const fillModal = (existing?: ExerciceLocal): void => {
    const annee = existing?.annee ?? currentYear;
    editingId = existing?.id ?? null;
    if (inputCode) inputCode.value = existing?.code ?? `EX-${annee}`;
    if (inputAnnee) inputAnnee.value = annee;
    if (inputDebut) inputDebut.value = existing?.date_debut ?? `${annee}-01-01`;
    if (inputFin) inputFin.value = existing?.date_fin ?? `${annee}-12-31`;
    if (inputStatut) inputStatut.value = existing?.statut ?? 'ouvert';
    if (inputComm) inputComm.value = existing?.commentaire ?? '';
    if (modal) modal.setAttribute('title', existing ? 'Modifier l’exercice' : 'Nouvel exercice');
  };

  const openEditor = (existing?: ExerciceLocal): void => {
    if (!guard(canMutate)) return;
    fillModal(existing);
    modal?.open();
  };

  const saveFromModal = async (): Promise<void> => {
    if (!guard(canMutate)) return;
    const code = inputCode?.value.trim() ?? '';
    const annee = String(inputAnnee?.value ?? '').slice(0, 4);
    const date_debut = inputDebut?.value ?? '';
    const date_fin = inputFin?.value ?? '';
    if (!code || !annee || !date_debut || !date_fin) {
      ToastService.warning('Code, année et dates sont requis');
      return;
    }
    if (date_fin < date_debut) {
      ToastService.warning('La date de fin précède la date de début');
      return;
    }
    const existing = editingId ? declared.find((x) => x.id === editingId) : undefined;
    const row: ExerciceLocal = {
      id: existing?.id.startsWith('derived-') ? uuid('exo') : (existing?.id ?? uuid('exo')),
      annee,
      code,
      commentaire: inputComm?.value.trim() || undefined,
      date_debut,
      date_fin,
      statut: inputStatut?.value === 'cloture' ? 'cloture' : 'ouvert',
    };
    declared = upsertById(
      declared.filter((x) => x.annee !== annee || x.id === row.id),
      row,
    );
    await saveRegister(tid, 'exercices', declared);
    modal?.close();
    render();
    ToastService.success('Exercice enregistré');
  };

  section.querySelector('#exo-modal-cancel')?.addEventListener('click', () => modal?.close());
  section.querySelector('#exo-modal-save')?.addEventListener('click', () => void saveFromModal());
  inputAnnee?.addEventListener('change', () => {
    const an = inputAnnee.value.slice(0, 4);
    if (!an) return;
    if (inputCode && (!inputCode.value || /^EX-\d{4}$/.test(inputCode.value))) {
      inputCode.value = `EX-${an}`;
    }
    if (inputDebut && !inputDebut.value) inputDebut.value = `${an}-01-01`;
    if (inputFin && !inputFin.value) inputFin.value = `${an}-12-31`;
  });

  bindCrudTable(
    section.querySelector('#exo-table tbody'),
    section,
    (id) => openEditor(declared.find((x) => x.id === id)),
    async (id) => {
      declared = removeById(declared, id);
      await saveRegister(tid, 'exercices', declared);
      render();
    },
    canMutate,
  );

  const w = window as unknown as Record<string, unknown>;
  w['openExerciceModal'] = () => openEditor();
};

export interface StockArticle {
  id: string;
  code: string;
  designation: string;
  type: string;
  quantite: number;
  pu: number;
  alerte: number;
}

export function valeurStock(articles: StockArticle[]): number {
  return articles.reduce((s, a) => s + a.quantite * a.pu, 0);
}

export const stocksController: ViewController = async (section, ctx, shell) => {
  const tid = tenantIdOf(ctx);
  const canMutate = canMutateComptaRecords(shell.permission);
  let articles = await loadRegister<StockArticle>(tid, 'stocks');

  const render = (): void => {
    const grid = section.querySelector<HTMLElement>('#stocks-kpi-grid');
    if (grid)
      grid.innerHTML =
        kpi('Articles', String(articles.length)) +
        kpi('Valeur du stock', fmtFCFA(valeurStock(articles)));
    const tbody = section.querySelector('#stocks-table tbody');
    if (!tbody) return;
    tbody.innerHTML =
      articles.length === 0
        ? emptyRow(8, 'Aucun article en stock.')
        : articles
            .map((a) => {
              const valeur = a.quantite * a.pu;
              const alert = a.alerte > 0 && a.quantite <= a.alerte;
              return `<tr>
                <td class="mono">${esc(a.code)}</td>
                <td>${esc(a.designation)}</td>
                <td>${esc(a.type)}</td>
                <td class="num">${a.quantite}</td>
                <td class="num">${esc(fmtFCFA(a.pu))}</td>
                <td class="num">${esc(fmtFCFA(valeur))}</td>
                <td>${alert ? '<span class="pill warning">Seuil</span>' : '<span class="pill success">OK</span>'}</td>
                <td class="num">${crudActions(a.id)}</td>
              </tr>`;
            })
            .join('');
  };
  render();

  const fields = (): FieldDef[] => [
    { kind: 'text', name: 'code', label: 'Code', required: true, placeholder: 'ART-001' },
    { kind: 'text', name: 'designation', label: 'Désignation', required: true },
    {
      kind: 'select',
      name: 'type',
      label: 'Type',
      options: [
        { value: 'marchandise', label: 'Marchandise' },
        { value: 'matiere', label: 'Matière première' },
        { value: 'fourniture', label: 'Fourniture' },
      ],
    },
    { kind: 'number', name: 'quantite', label: 'Quantité', min: 0 },
    { kind: 'number', name: 'pu', label: 'PU moyen (FCFA)', min: 0 },
    { kind: 'number', name: 'alerte', label: 'Seuil d’alerte', min: 0, required: false },
  ];

  const openEditor = (existing?: StockArticle): void => {
    if (!guard(canMutate)) return;
    openRegisterForm({
      parent: section,
      title: existing ? 'Modifier l’article' : 'Nouvel article',
      fields: fields(),
      values: existing,
      onSubmit: async (data) => {
        const row: StockArticle = {
          id: existing?.id ?? uuid('stk'),
          code: String(data['code']),
          designation: String(data['designation']),
          type: String(data['type']),
          quantite: Number(data['quantite'] ?? 0),
          pu: Number(data['pu'] ?? 0),
          alerte: Number(data['alerte'] ?? 0),
        };
        articles = upsertById(articles, row);
        await saveRegister(tid, 'stocks', articles);
        render();
      },
    });
  };

  bindCrudTable(
    section.querySelector('#stocks-table tbody'),
    section,
    (id) => openEditor(articles.find((x) => x.id === id)),
    async (id) => {
      articles = removeById(articles, id);
      await saveRegister(tid, 'stocks', articles);
      render();
    },
    canMutate,
  );

  const w = window as unknown as Record<string, unknown>;
  w['openStockModal'] = () => openEditor();
};

export interface ImmobilisationLocal {
  id: string;
  type: string;
  designation: string;
  salarie_id: string;
  valeur: number;
  an_mensuel: number;
  date_acquisition: string;
  tva: 'oui' | 'non';
  statut: 'active' | 'cedee' | 'amortie';
}

export const immobilisationsController: ViewController = async (section, ctx, shell) => {
  const tid = tenantIdOf(ctx);
  const canMutate = canMutateComptaRecords(shell.permission);
  const salaries = await ctx.salaries.list();
  let items = await loadRegister<ImmobilisationLocal>(tid, 'immobilisations');

  const render = (): void => {
    const grid = section.querySelector<HTMLElement>('#immo-kpi-grid');
    const brute = items.reduce((s, i) => s + (i.valeur || 0), 0);
    if (grid)
      grid.innerHTML =
        kpi('Immobilisations', String(items.length)) + kpi('Valeur brute', fmtFCFA(brute));
    const tbody = section.querySelector('#immo-table tbody');
    const empty = section.querySelector<HTMLElement>('#immo-empty');
    if (empty) empty.style.display = items.length === 0 ? '' : 'none';
    if (!tbody) return;
    tbody.innerHTML =
      items.length === 0
        ? ''
        : items
            .map(
              (i) => `<tr>
          <td>${esc(i.type)}</td>
          <td>${esc(i.designation)}</td>
          <td>${esc(salarieLabel(salaries, i.salarie_id))}</td>
          <td class="num">${esc(fmtFCFA(i.valeur))}</td>
          <td class="num">${esc(fmtFCFA(i.an_mensuel))}</td>
          <td>${esc(fmtDate(i.date_acquisition))}</td>
          <td>${i.tva === 'oui' ? 'Oui' : 'Non'}</td>
          <td><span class="pill ${i.statut === 'active' ? 'success' : ''}">${esc(i.statut)}</span></td>
          <td class="num">${crudActions(i.id)}</td>
        </tr>`,
            )
            .join('');
  };
  render();

  const fields = (): FieldDef[] => [
    {
      kind: 'select',
      name: 'type',
      label: 'Type',
      options: [
        { value: 'vehicule', label: 'Véhicule' },
        { value: 'logement', label: 'Logement' },
        { value: 'materiel', label: 'Matériel' },
      ],
    },
    { kind: 'text', name: 'designation', label: 'Désignation', required: true },
    {
      kind: 'select',
      name: 'salarie_id',
      label: 'Salarié bénéficiaire',
      options:
        salaries.length > 0
          ? salaries.map((s) => ({ value: s.id, label: `${s.prenom} ${s.nom}` }))
          : [{ value: '', label: 'Aucun salarié' }],
    },
    { kind: 'number', name: 'valeur', label: 'Valeur d’achat', min: 0 },
    { kind: 'number', name: 'an_mensuel', label: 'AN mensuel', min: 0 },
    { kind: 'date', name: 'date_acquisition', label: 'Acquise le', required: true },
    {
      kind: 'select',
      name: 'tva',
      label: 'TVA applicable',
      options: [
        { value: 'non', label: 'Non' },
        { value: 'oui', label: 'Oui' },
      ],
    },
    {
      kind: 'select',
      name: 'statut',
      label: 'Statut',
      options: [
        { value: 'active', label: 'Active' },
        { value: 'amortie', label: 'Amortie' },
        { value: 'cedee', label: 'Cédée' },
      ],
    },
  ];

  const openEditor = (existing?: ImmobilisationLocal): void => {
    if (!guard(canMutate)) return;
    openRegisterForm({
      parent: section,
      title: existing ? 'Modifier l’immobilisation' : 'Nouvelle immobilisation',
      fields: fields(),
      values: existing,
      onSubmit: async (data) => {
        const row: ImmobilisationLocal = {
          id: existing?.id ?? uuid('immo'),
          type: String(data['type']),
          designation: String(data['designation']),
          salarie_id: String(data['salarie_id'] ?? ''),
          valeur: Number(data['valeur'] ?? 0),
          an_mensuel: Number(data['an_mensuel'] ?? 0),
          date_acquisition: String(data['date_acquisition']),
          tva: data['tva'] === 'oui' ? 'oui' : 'non',
          statut: (String(data['statut']) as ImmobilisationLocal['statut']) || 'active',
        };
        items = upsertById(items, row);
        await saveRegister(tid, 'immobilisations', items);
        render();
      },
    });
  };

  bindCrudTable(
    section.querySelector('#immo-table tbody'),
    section,
    (id) => openEditor(items.find((x) => x.id === id)),
    async (id) => {
      items = removeById(items, id);
      await saveRegister(tid, 'immobilisations', items);
      render();
    },
    canMutate,
  );

  const w = window as unknown as Record<string, unknown>;
  w['openImmoModal'] = () => openEditor();
  w['openImmoAnModal'] = () => openEditor();

  let comptables = await loadRegister<Immobilisation>(tid, 'immobilisations_comptables');
  const anneeCourante = new Date().getFullYear();

  const renderComptable = (): void => {
    const tbody = section.querySelector('#immo-comptable-table tbody');
    const empty = section.querySelector<HTMLElement>('#immo-comptable-empty');
    if (empty) empty.style.display = comptables.length === 0 ? '' : 'none';
    if (!tbody) return;
    tbody.innerHTML =
      comptables.length === 0
        ? ''
        : comptables
            .map((i) => {
              const dot = AmortissementsService.dotationPourAnneeCivile(i, anneeCourante);
              const vnc = AmortissementsService.vncFinExercice(
                i,
                anneeCourante -
                  Number((i.date_mise_service || i.date_acquisition).slice(0, 4)) +
                  1,
              );
              return `<tr>
          <td>${esc(i.categorie)}</td>
          <td>${esc(i.libelle)}</td>
          <td class="num">${esc(fmtFCFA(i.valeur_acquisition))}</td>
          <td>${esc(fmtDate(i.date_mise_service || i.date_acquisition))}</td>
          <td>${i.duree_amortissement_annees} ans</td>
          <td class="num">${esc(fmtFCFA(dot))}</td>
          <td class="num">${esc(fmtFCFA(vnc))}</td>
          <td><span class="pill ${i.cede ? '' : 'success'}">${i.cede ? 'Cédée' : 'Active'}</span></td>
          <td class="num">${crudActions(i.id)}${
            canMutate && !i.cede
              ? `<button type="button" class="btn btn-ghost" data-ceder="${esc(i.id)}">Céder</button>`
              : ''
          }</td>
        </tr>`;
            })
            .join('');
  };
  renderComptable();

  const openComptableEditor = (existing?: Immobilisation): void => {
    if (!guard(canMutate)) return;
    openRegisterForm({
      parent: section,
      title: existing ? 'Modifier l’immobilisation comptable' : 'Nouvelle immobilisation comptable',
      fields: [
        {
          kind: 'select',
          name: 'categorie',
          label: 'Catégorie',
          options: [
            { value: 'informatique', label: 'Informatique (3 ans)' },
            { value: 'materiel', label: 'Matériel (5 ans)' },
            { value: 'vehicule', label: 'Véhicule (5 ans)' },
            { value: 'mobilier', label: 'Mobilier (10 ans)' },
            { value: 'batiment', label: 'Bâtiment (20 ans)' },
            { value: 'incorporel', label: 'Incorporel (5 ans)' },
            { value: 'terrain', label: 'Terrain (non amorti)' },
          ],
        },
        { kind: 'text', name: 'libelle', label: 'Libellé', required: true },
        { kind: 'text', name: 'code', label: 'Code', required: true },
        { kind: 'number', name: 'valeur_acquisition', label: 'Valeur d’acquisition', min: 0 },
        { kind: 'number', name: 'valeur_residuelle', label: 'Valeur résiduelle', min: 0 },
        { kind: 'date', name: 'date_acquisition', label: 'Acquise le', required: true },
        { kind: 'date', name: 'date_mise_service', label: 'Mise en service', required: true },
        {
          kind: 'select',
          name: 'mode',
          label: 'Mode',
          options: [
            { value: 'lineaire', label: 'Linéaire' },
            { value: 'degressif', label: 'Dégressif' },
          ],
        },
      ],
      values: existing
        ? {
            ...existing,
            valeur_acquisition: existing.valeur_acquisition,
            valeur_residuelle: existing.valeur_residuelle ?? 0,
          }
        : undefined,
      onSubmit: async (data) => {
        const categorie = String(data['categorie']) as CategorieImmo;
        const comptes = comptesImmobilisation(categorie);
        const dateAcq = String(data['date_acquisition']);
        const row: Immobilisation = {
          id: existing?.id ?? uuid('immo-c'),
          tenant_id: tid,
          code: String(data['code'] ?? '').trim() || (existing?.code ?? uuid('IM')),
          libelle: String(data['libelle']),
          categorie,
          date_acquisition: dateAcq,
          date_mise_service: String(data['date_mise_service'] || dateAcq),
          valeur_acquisition: Number(data['valeur_acquisition'] ?? 0),
          valeur_residuelle: Number(data['valeur_residuelle'] ?? 0) || undefined,
          duree_amortissement_annees:
            existing && existing.categorie === categorie
              ? existing.duree_amortissement_annees
              : dureeAmortissement(categorie),
          mode: (String(data['mode']) as ModeAmortissement) || 'lineaire',
          compte_immo: existing?.compte_immo ?? comptes.compte_immo,
          compte_amortissement: existing?.compte_amortissement ?? comptes.compte_amortissement,
          compte_dotation: existing?.compte_dotation ?? comptes.compte_dotation,
          cede: existing?.cede,
          date_cession: existing?.date_cession,
          valeur_cession: existing?.valeur_cession,
          cree_le: existing?.cree_le ?? new Date().toISOString(),
        };
        comptables = upsertById(comptables, row);
        await saveRegister(tid, 'immobilisations_comptables', comptables);
        if (!existing) {
          for (const ecr of genererEcrituresAcquisition(row)) {
            await ctx.ecritures.add(ecr);
          }
        }
        render();
        renderComptable();
      },
    });
  };

  bindCrudTable(
    section.querySelector('#immo-comptable-table tbody'),
    section,
    (id) => openComptableEditor(comptables.find((x) => x.id === id)),
    async (id) => {
      comptables = removeById(comptables, id);
      await saveRegister(tid, 'immobilisations_comptables', comptables);
      render();
      renderComptable();
    },
    canMutate,
  );

  section.querySelector('#immo-comptable-table')?.addEventListener('click', (ev) => {
    const btn = (ev.target as HTMLElement).closest<HTMLButtonElement>('[data-ceder]');
    const id = btn?.dataset.ceder;
    if (!id || !guard(canMutate)) return;
    const immo = comptables.find((x) => x.id === id);
    if (!immo || immo.cede) return;
    openRegisterForm({
      parent: section,
      title: `Céder ${immo.libelle}`,
      fields: [
        { kind: 'date', name: 'date_cession', label: 'Date de cession', required: true },
        { kind: 'number', name: 'valeur_cession', label: 'Prix de cession', min: 0 },
      ],
      onSubmit: async (data) => {
        const row: Immobilisation = {
          ...immo,
          cede: true,
          date_cession: String(data['date_cession']),
          valeur_cession: Number(data['valeur_cession'] ?? 0),
        };
        comptables = upsertById(comptables, row);
        await saveRegister(tid, 'immobilisations_comptables', comptables);
        for (const ecr of genererEcrituresCession(row)) {
          await ctx.ecritures.add(ecr);
        }
        render();
        renderComptable();
      },
    });
  });

  w['openImmoComptableModal'] = () => openComptableEditor();
  w['passerDotationsImmo'] = async () => {
    if (!guard(canMutate)) return;
    try {
      const existantes = await ctx.ecritures.list();
      let ajoutees = 0;
      for (const immo of comptables) {
        const deja = existantes.some(
          (e) =>
            e.source === 'amortissement' &&
            e.groupe_id === immo.id &&
            (e.periode || '').startsWith(String(anneeCourante)),
        );
        if (deja) continue;
        const lignes = genererEcrituresDotation(immo, anneeCourante);
        for (const ecr of lignes) {
          await ctx.ecritures.add(ecr);
          ajoutees += 1;
        }
      }
      ToastService.success(
        ajoutees > 0
          ? `${ajoutees} écriture(s) de dotation ${anneeCourante} générée(s)`
          : `Aucune dotation nouvelle pour ${anneeCourante}`,
      );
    } catch (err) {
      const msg =
        err instanceof AuthError && err.code === 'LICENCE_EXPIRED'
          ? expiredLicenceCopy().body
          : err instanceof Error
            ? err.message
            : 'Impossible de passer les dotations';
      ToastService.error(msg);
    }
  };
};
